// ✅ FILE: src/components/CartPopup.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { API_BASE, SHEETS, forceRefreshSheet } from "../utils/api";
import { getPendingKg } from "../utils/orders";
import { usePolling } from "../utils/usePolling";

// ───────────────────────────────────────────────────────────────────────────
//  Autocomplete history helpers — remember what the dealer typed into
//  free-text order fields (Vehicle, Delivery Area, Driver Contact, Broker
//  Name) across submissions, then suggest those values next time. Storage
//  is keyed per-dealer-phone so different users sharing a device don't see
//  each other's history.
// ───────────────────────────────────────────────────────────────────────────
const HISTORY_MAX_ENTRIES = 50;

const historyKey = (phone, field) =>
  `cart-history:${phone || "anon"}:${field}`;

const readHistory = (phone, field) => {
  try {
    const raw = localStorage.getItem(historyKey(phone, field));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
};

const writeHistoryEntry = (phone, field, value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return;
  try {
    const existing = readHistory(phone, field);
    // Most-recent-first, deduped case-insensitively
    const lower = trimmed.toLowerCase();
    const filtered = existing.filter((s) => s.toLowerCase() !== lower);
    const updated = [trimmed, ...filtered].slice(0, HISTORY_MAX_ENTRIES);
    localStorage.setItem(historyKey(phone, field), JSON.stringify(updated));
  } catch {
    /* ignore quota errors */
  }
};

/**
 * AutocompleteInput — text input that remembers past entries per-field
 * and suggests them on focus / while typing. Acts like a browser
 * datalist but with our own styling and substring matching (browser
 * datalists only match prefix on some platforms).
 *
 * Props:
 *   value          — current input value (controlled)
 *   onChange(v)    — value setter
 *   fieldKey       — unique key for the history bucket (e.g. "vehicle")
 *   phone          — dealer phone, scopes history per-user
 *   placeholder    — placeholder text
 *   className      — input class names
 *   maxLength      — optional max length
 *   inputMode      — optional inputMode
 *   onlyDigits     — if true, strip non-digits from typed input
 */
function AutocompleteInput({
  value,
  onChange,
  fieldKey,
  phone,
  placeholder,
  className,
  maxLength,
  inputMode,
  onlyDigits = false,
}) {
  const [history, setHistory] = useState(() => readHistory(phone, fieldKey));
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Reload history whenever phone/field key changes (rare but cheap)
  useEffect(() => {
    setHistory(readHistory(phone, fieldKey));
  }, [phone, fieldKey]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Filter suggestions:
  //  - empty input → show all history
  //  - non-empty   → show only entries that contain the input (case-insensitive)
  //                  AND aren't exactly equal to it (so the dropdown isn't a
  //                  redundant echo of what's already in the field)
  const suggestions = useMemo(() => {
    const q = String(value || "").trim().toLowerCase();
    if (!history.length) return [];
    if (!q) return history.slice(0, 10);
    return history
      .filter(
        (s) =>
          s.toLowerCase().includes(q) && s.toLowerCase() !== q
      )
      .slice(0, 10);
  }, [history, value]);

  const handleType = (raw) => {
    let v = raw;
    if (onlyDigits) v = v.replace(/\D/g, "");
    if (maxLength) v = v.slice(0, maxLength);
    onChange(v);
    setOpen(true);
    setHighlighted(-1);
  };

  const pick = (entry) => {
    onChange(entry);
    setOpen(false);
    setHighlighted(-1);
    // Move history entry to the top for "most recent" UX
    writeHistoryEntry(phone, fieldKey, entry);
    setHistory(readHistory(phone, fieldKey));
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && highlighted >= 0) {
      e.preventDefault();
      pick(suggestions[highlighted]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value || ""}
        onChange={(e) => handleType(e.target.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={className}
        inputMode={inputMode}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul
          className="absolute left-0 right-0 top-full mt-1 z-30 max-h-56 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl"
          // Prevent the field's onBlur from firing before the click registers
          onMouseDown={(e) => e.preventDefault()}
        >
          {suggestions.map((s, i) => (
            <li
              key={s + i}
              onClick={() => pick(s)}
              className={`px-4 py-2 text-sm cursor-pointer ${
                i === highlighted
                  ? "bg-yellow-100 text-gray-900"
                  : "hover:bg-gray-50 text-gray-800"
              }`}
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function CartPopup({ open, onClose, cartItems = [], setCart }) {
  const navigate = useNavigate();

  const LOGIN_DETAILS_URL = SHEETS.dealers();

  const SEND_OTP_URL = `${API_BASE}/api/send-otp`;

  const SHEET_URL = SHEETS.products;

  // ✅ Special stock URL for TMT / BW / Rings (served from AWS cache)
  const STOCK_URL_CART = SHEETS.stock;

  // Structure orders are ALSO written straight into the orders sheet via its
  // Apps Script web app, in addition to the Google Form below. Unlike the
  // form (posted `no-cors`, so a failed submit is invisible), this POST is
  // retried and its reply is checked; the sheet ignores a duplicate Request
  // id, so a retry can never double-write.
  //
  // Empty = sheet write off, Google Form only.
  const STRUCTURE_SHEET_URL =
    "https://script.google.com/macros/s/AKfycbxf73ADfuwKX4ZAboFEkL2NB0cM_FGRh6kt67Rkq8SEgdPchKsnE5oA-HRalaOCdes/exec";

  const FORM_URLS = {
    structure:
      "https://docs.google.com/forms/d/e/1FAIpQLSdE4O48EWqvdRh-535JHIZ5ILOkBzOzOE9tBS5LdRLlrMalKQ/formResponse",
    mm6: "https://docs.google.com/forms/d/e/1FAIpQLSc5AwWyY0j1m847_-pXBsMx-ONeVmrgPa3qw19omfqCK3xHTg/formResponse",
    binding:
      "https://docs.google.com/forms/d/e/1FAIpQLSdKDB9sgsHxbq1KVFzCs_GCRPengNV2DjOekEAMrycPhmIcuQ/formResponse",
    rings:
      "https://docs.google.com/forms/d/e/1FAIpQLSdFrCrOaIgnCdcBZk0UY__pFlcnMzfZkMBsaSK2XmHeyMAb1A/formResponse",
  };

  const MM6_FINAL_ENTRY = "entry.1339341795";
  const BINDING_FINAL_ENTRY = "entry.1737542961";
  const RINGS_FINAL_ENTRY = "entry.230158675";

  const MM6_STATUS_ENTRY = "entry.247730246";
  const BINDING_STATUS_ENTRY = "entry.599332797";
  const RINGS_STATUS_ENTRY = "entry.1067555636";

  const IS_LOCALHOST =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "so.friendssteel.com");

  const DEMO_OTP_ENABLED = IS_LOCALHOST;
  const DEMO_PHONE = "9898989898";
  const DEMO_OTP = "1254";

  const ALL_TYPES = ["dealer", "nbd", "staff", "cre", "contractor", "admin"];

  const firmRef = useRef(null);
  const otpRef = useRef(null);
  const paymentRefs = useRef({
    structure: null,
    mm6: null,
    binding: null,
    rings: null,
  });

  const scrollFocus = (el) => {
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => el.focus?.(), 180);
    } catch {
      /* ignore */
    }
  };

  const [firmsCache, setFirmsCache] = useState({});
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [demoOtpValue, setDemoOtpValue] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const REQ_MSG = "This field is required";
  const showReq = (cond) => submitAttempted && cond;

  const normalizePhone = (v) =>
    String(v ?? "")
      .replace(/\D/g, "")
      .slice(-10);
  const normalizeType = (v) =>
    String(v ?? "")
      .trim()
      .toLowerCase();
  const normalizeText = (v) => String(v ?? "").trim();

  const toNumberSafe = (v) => {
    const n = Number(
      String(v ?? "")
        .replace(/,/g, "")
        .trim(),
    );
    return Number.isFinite(n) ? n : 0;
  };

  const num = (v) => {
    const n = Number(
      String(v ?? "")
        .replace(/,/g, "")
        .trim(),
    );
    return Number.isFinite(n) ? n : 0;
  };

  const formatRate = (value) => Number(value || 0).toFixed(2);
  const toThousandRate = (value) => Math.round(Number(value || 0) * 1000);

  const cleanSizeName = (name = "") => {
    let s = String(name || "").trim();
    s = s.replace(/^TMT\s*-\s*/i, "").trim();
    return s;
  };

  const creditToRupees = (row) => {
    const lakh =
      row?.["Credit Limit In Lakh"] ??
      row?.["Credit Limit In Lacs"] ??
      row?.["Credit Limit (Lakh)"] ??
      row?.["Credit Limit (Lacs)"] ??
      row?.["Credit Limit"] ??
      row?.["Credit Limit "] ??
      row?.creditLimit ??
      row?.CreditLimit ??
      0;
    return toNumberSafe(lakh) * 100000;
  };

  // ✅ Normalize ring size key for stock-map lookups
  const normalizeRingStockKeyCart = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/kgs?/g, "kg")
      .replace(/(\d)\s*mm\b/g, "$1mm")
      .replace(/\s*x\s*/g, "x")
      .replace(/\s+/g, " ")
      .trim();

  // ✅ Normalize size key for TMT / BW
  const normSizeKeyCart = (v) =>
    String(v || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/kgs?/g, "kg")
      .replace(/\s*x\s*/g, "x");

  /**
   * ✅ FIXED: single source of truth for reading stock from any cart item.
   * Priority: latestStock → stock (if > 0) → availableStock
   * Does NOT use ?? so explicit 0 falls through correctly.
   */
  const getItemStock = (item) => {
    if (item?.latestStock !== undefined && item.latestStock !== null) {
      return Number(item.latestStock) || 0;
    }
    if (
      item?.stock !== undefined &&
      item.stock !== null &&
      Number(item.stock) > 0
    ) {
      return Number(item.stock);
    }
    return Number(item?.availableStock) || 0;
  };

  // ─── state ────────────────────────────────────────────────────────────────
  const [firms, setFirms] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState("");
  const [firmDetails, setFirmDetails] = useState({
    gst: "",
    contact: "",
    creditLimit: "",
    outstanding: "",
    contactPerson: "",
    area: "",
    zone: "",
    district: "",
  });
  const [loading, setLoading] = useState(true);
  const [liveOutstandings, setLiveOutstandings] = useState({});
  const [fetchingLive, setFetchingLive] = useState(true);

  const emptyForm = {
    vehicle: "",
    deliveryArea: "",
    driverContact: "",
    loadingDate: "",
    paymentTerms: "",
    brokerName: "",
    remarks: "",
  };

  // Per-dealer-phone key for persisted form data. Read at lazy init so we
  // don't have to wait for an effect — the form fields show the user's
  // last-entered values immediately when the cart re-mounts.
  const formDataLsKey = (() => {
    try {
      const d = JSON.parse(localStorage.getItem("dealer") || "{}");
      const ph = String(d?.phone ?? d?.["Contact No"] ?? "")
        .replace(/\D/g, "")
        .slice(-10);
      return `cart-form-data:${ph || "anon"}`;
    } catch {
      return "cart-form-data:anon";
    }
  })();

  const [formData, setFormData] = useState(() => {
    // Restore last-typed values across cart close / product navigation.
    // Cleared after a successful order submit (see resetForms in handler).
    try {
      const raw = localStorage.getItem(formDataLsKey);
      if (raw) {
        const saved = JSON.parse(raw);
        return {
          structure: { ...emptyForm, ...(saved?.structure || {}) },
          mm6: { ...emptyForm, ...(saved?.mm6 || {}) },
          binding: { ...emptyForm, ...(saved?.binding || {}) },
          rings: { ...emptyForm, ...(saved?.rings || {}) },
        };
      }
    } catch {
      /* fall through to empty form */
    }
    return {
      structure: { ...emptyForm },
      mm6: { ...emptyForm },
      binding: { ...emptyForm },
      rings: { ...emptyForm },
    };
  });

  // Persist formData on every change. Skip the write when EVERY tab is
  // back to empty (avoids storing an essentially-blank object).
  useEffect(() => {
    try {
      const isAllEmpty = Object.values(formData).every((tab) =>
        Object.values(tab || {}).every((v) => !v)
      );
      if (isAllEmpty) {
        localStorage.removeItem(formDataLsKey);
      } else {
        localStorage.setItem(formDataLsKey, JSON.stringify(formData));
      }
    } catch {
      /* quota errors are non-fatal */
    }
  }, [formData, formDataLsKey]);

  // When the cart opens right after a scan / Order-Form "Add to Cart", set
  // the Payment Term + Vehicle No from that order. We OVERWRITE (not just
  // fill-if-empty) so a previous order's stale value is cleared — e.g. if
  // this order had no vehicle, the Vehicle field is emptied, not left filled.
  useEffect(() => {
    if (!open) return;
    let meta = null;
    try {
      const raw = localStorage.getItem("scanCartMeta:v1");
      if (raw) meta = JSON.parse(raw);
    } catch {
      /* ignore */
    }
    if (!meta) return;
    setFormData((prev) => {
      const next = { ...prev };
      ["structure", "mm6", "binding", "rings"].forEach((k) => {
        const tab = { ...(next[k] || {}) };
        // Vehicle: reflect exactly what this order had ("" clears stale).
        tab.vehicle = meta.vehicleNo || "";
        // Payment term: only set when this order actually had one.
        if (meta.paymentTerm) tab.paymentTerms = meta.paymentTerm;
        next[k] = tab;
      });
      return next;
    });
    try {
      localStorage.removeItem("scanCartMeta:v1");
    } catch {
      /* ignore */
    }
  }, [open]);

  const [errorsByTab, setErrorsByTab] = useState({
    structure: { vehicle: "" },
    mm6: { vehicle: "" },
    binding: { vehicle: "" },
    rings: { vehicle: "" },
  });

  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");

  const [editingIndex, setEditingIndex] = useState(null);
  const [editQty, setEditQty] = useState("");
  const [qtyError, setQtyError] = useState("");

  const [bookingClosedItems, setBookingClosedItems] = useState([]);

  const [successOpen, setSuccessOpen] = useState(false);
  const [successRequestIds, setSuccessRequestIds] = useState({
    structure: "",
    mm6: "",
    binding: "",
    rings: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loggedAccount = JSON.parse(localStorage.getItem("dealer") || "{}");

  const storedPhone = normalizePhone(
    loggedAccount?.phone ??
      loggedAccount?.["Contact No"] ??
      loggedAccount?.["Contact No "] ??
      loggedAccount?.mobile ??
      loggedAccount?.Mobile ??
      loggedAccount?.Phone,
  );

  const [loggedProfile, setLoggedProfile] = useState(null);
  const [scriptRows, setScriptRows] = useState([]);
  const [loginPhoneFromScript, setLoginPhoneFromScript] = useState("");
  const [latestMap, setLatestMap] = useState({});
  const [sheetFetchedAt, setSheetFetchedAt] = useState(null);
  const [isSyncingSheet, setIsSyncingSheet] = useState(false);
  const [sheetSyncOk, setSheetSyncOk] = useState(false);

  // ─── helpers ──────────────────────────────────────────────────────────────
  const getItemStdName = (item) =>
    normalizeText(item?.size) ||
    normalizeText(item?.standardName) ||
    normalizeText(item?.name) ||
    "";

  const getItemTypeCode = (item) =>
    normalizeText(item?.type || item?.TYPE || "");

  const buildKey = (typeCode, stdName) => {
    const t = normalizeText(typeCode);
    const s = normalizeText(stdName);
    if (!s) return "";
    return t ? `${t}||${s}` : s;
  };

  const pickFirst = (obj, keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
    return "";
  };

  const getDistrictValue = () => {
    const dealer = loggedProfile || loggedAccount || {};
    const localDealer = JSON.parse(localStorage.getItem("dealer") || "{}");
    return (
      normalizeText(
        dealer?.District ||
          dealer?.district ||
          dealer?.DISTRICT ||
          dealer?.["District"] ||
          dealer?.["District "] ||
          localDealer?.District ||
          localDealer?.district ||
          localDealer?.["District"] ||
          localDealer?.["District "] ||
          dealer?.["STR District"] ||
          dealer?.["STR DISTRICT"] ||
          dealer?.["SDistrict"],
      ) || ""
    );
  };

  const getMm6SizeLabel = (item) => {
    const raw = normalizeText(
      item?.size || item?.name || item?.standardName || "",
    );
    const clean = raw.toLowerCase();
    if (clean === "30 to 40" || clean === "30-40" || clean.includes("30 to 40"))
      return "30 to 40";
    if (clean === "below 30" || clean.includes("below 30")) return "Below 30";
    return cleanSizeName(raw);
  };

  // Binding wire size = the variant (MS / GI) from the cart item name.
  const getBindingSizeLabel = (item) => {
    const raw = item?.meta?.sizeLabel || item?.size || item?.name || "";
    const s = String(raw).replace(/^Binding Wire\s*-\s*/i, "").trim();
    return s || "Binding Wire";
  };

  // ─── reset on open ────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      setOtp("");
      setOtpSent(false);
      setTimer(0);
      setCanResend(true);
      setEditingIndex(null);
      setEditQty("");
      setQtyError("");
      setSubmitAttempted(false);
      setMessage("");
      setMessageType("success");
      setBookingClosedItems([]);
      setSuccessOpen(false);
      setSuccessRequestIds({ structure: "", mm6: "", binding: "", rings: "" });
      setDemoOtpValue("");
      setSheetSyncOk(false);
      setIsSyncingSheet(false);
      setIsSubmitting(false);
    }
  }, [open]);

  // ─── firm cache ───────────────────────────────────────────────────────────
  // Two thresholds:
  //   FIRMS_FRESH_MS  — cache this fresh skips the network call entirely.
  //                     We trust the backend's 2-min auto-sync + recent
  //                     fetch (e.g. from Login) to have the latest data.
  //   FIRMS_LS_TTL_MS — cache stale-but-usable; we still render from it
  //                     instantly while a fresh fetch runs in background.
  const FIRMS_FRESH_MS = 90 * 1000;       // 90 sec → skip refetch
  const FIRMS_LS_TTL_MS = 10 * 60 * 1000; // 10 min → still render

  const readFirmCacheLS = (phone10) => {
    try {
      if (!phone10) return null;
      const raw = localStorage.getItem(`firmsCache:v1:${phone10}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.ts || !parsed?.payload) return null;
      if (Date.now() - parsed.ts > FIRMS_LS_TTL_MS) return null;
      return parsed.payload;
    } catch {
      return null;
    }
  };

  const writeFirmCacheLS = (phone10, payload) => {
    try {
      if (!phone10) return;
      localStorage.setItem(
        `firmsCache:v1:${phone10}`,
        JSON.stringify({ ts: Date.now(), payload }),
      );
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!open) return;
    console.time("[cart] open total");

    const cacheKey = storedPhone;

    const applyCache = (cached) => {
      setScriptRows(cached.rows || []);
      setFirms(cached.firms || []);
      setSelectedFirm(
        (cached.firms?.length > 0 && cached.firms[0].firmName) || "",
      );
      setLoggedProfile(cached.profile || loggedAccount);
      setLoginPhoneFromScript(cached.loginPhone || "");
      setLoading(false);
      setHasLoadedOnce(true);
    };

    console.time("[cart] cache apply");

    // Compute cache age so we can decide whether to skip the network call
    // entirely. If anything fetched the same data recently (Login or a
    // previous cart open), there's nothing to gain by hitting the network
    // again — backend's 2-min auto-sync keeps the upstream cache fresh.
    let cacheAgeMs = Infinity;
    let lsCachePayload = null;
    if (cacheKey) {
      try {
        const raw = localStorage.getItem(`firmsCache:v1:${cacheKey}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.ts && parsed?.payload) {
            cacheAgeMs = Date.now() - parsed.ts;
            // ALWAYS render from cache instantly (even if stale) so the cart
            // never shows a blocking spinner. Stale data is refreshed by the
            // background fetch below — the user just sees the cart open fast.
            lsCachePayload = parsed.payload;
          }
        }
      } catch {
        /* ignore corrupt cache */
      }
    }

    if (cacheKey && firmsCache[cacheKey]) applyCache(firmsCache[cacheKey]);
    else if (lsCachePayload) applyCache(lsCachePayload);
    console.timeEnd("[cart] cache apply");

    const cacheIsFresh = cacheAgeMs < FIRMS_FRESH_MS;

    const fetchScriptData = async () => {
      try {
        // Only show the spinner if we DON'T have anything cached. With
        // cache, the cart popup is fully interactive immediately and the
        // background fetch refreshes data silently when it completes.
        if (!hasLoadedOnce) setLoading(true);
        if (!storedPhone) {
          setLoggedProfile(null);
          setLoginPhoneFromScript("");
          setScriptRows([]);
          setFirms([]);
          setSelectedFirm("");
          setLoading(false);
          return;
        }

        // FIRE-AND-FORGET — DON'T await this. Apps Script round-trips are
        // 3-15s and would block the cart UI from opening. The rate-limiter
        // in forceRefreshSheet means rapid successive opens won't actually
        // re-hit Apps Script (one refresh per 30s per key max). The backend
        // also auto-syncs dealers every 120s, so the cache is rarely more
        // than a couple minutes stale anyway.
        forceRefreshSheet("dealers").catch(() => {});

        console.time("[cart] GET dealers?phone");
        const res = await fetch(`${LOGIN_DETAILS_URL}?phone=${storedPhone}`, {
          cache: "no-store",
        });
        const data = await res.json();
        console.timeEnd("[cart] GET dealers?phone");
        console.timeEnd("[cart] open total");

        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.rows)
              ? data.rows
              : Array.isArray(data?.result)
                ? data.result
                : data
                  ? [data]
                  : [];

        const foundProfile =
          rows.find((r) => normalizePhone(r?.["Contact No"]) === storedPhone) ||
          rows.find(
            (r) => normalizePhone(r?.["Contact No "]) === storedPhone,
          ) ||
          rows.find((r) => normalizePhone(r?.phone) === storedPhone) ||
          rows.find((r) => normalizePhone(r?.mobile) === storedPhone) ||
          null;

        let phoneFromScript = "";
        let normalizedProfile = null;

        if (foundProfile) {
          phoneFromScript = normalizePhone(
            foundProfile?.["Contact No"] ??
              foundProfile?.["Contact No "] ??
              foundProfile?.phone ??
              foundProfile?.mobile,
          );
          const typeLower = normalizeType(
            foundProfile?.Type ?? foundProfile?.type,
          );
          normalizedProfile = {
            ...foundProfile,
            phone: phoneFromScript,
            type: typeLower,
          };
          setLoginPhoneFromScript(phoneFromScript);
          setLoggedProfile(normalizedProfile);
          localStorage.setItem("dealer", JSON.stringify(normalizedProfile));
        } else {
          setLoginPhoneFromScript("");
          setLoggedProfile(loggedAccount);
          normalizedProfile = loggedAccount;
        }

        const myRows = rows.filter((r) => {
          const p =
            normalizePhone(r?.["Contact No"]) ||
            normalizePhone(r?.["Contact No "]) ||
            normalizePhone(r?.phone) ||
            normalizePhone(r?.mobile);
          return p === storedPhone;
        });

        const mappedFirms = myRows
          .map((r) => ({
            firmName:
              normalizeText(
                r?.["Particulars"] ||
                  r?.["Firm Name"] ||
                  r?.["Firm Name "] ||
                  r?.firmName ||
                  r?.FirmName,
              ) || "",
            gst:
              normalizeText(
                r?.["GSTIN/UIN"] ||
                  r?.["GST No"] ||
                  r?.["GST No "] ||
                  r?.gst ||
                  r?.GST,
              ) || "",
            contact:
              normalizeText(
                r?.["Contact No"] ||
                  r?.["Contact No "] ||
                  r?.contact ||
                  r?.phone ||
                  r?.Phone,
              ) || "",
            creditLimit: String(creditToRupees(r)),
            outstanding:
              normalizeText(
                r?.["Outstanding"] ||
                  r?.["Outstanding "] ||
                  r?.outstanding ||
                  r?.OutStanding,
              ) || "0",
            contactPerson:
              normalizeText(
                r?.["Contact Person Name"] ||
                  r?.["Contact Person"] ||
                  r?.["Contact Person "] ||
                  r?.contactPerson ||
                  r?.ContactPerson,
              ) || "",
            area: normalizeText(r?.Area || r?.area || "") || "",
            zone:
              normalizeText(r?.ZONE || r?.Zone || r?.zone || r?.["ZONE "]) ||
              "",
            district:
              normalizeText(
                r?.District ||
                  r?.district ||
                  r?.DISTRICT ||
                  r?.["District"] ||
                  r?.["District "] ||
                  r?.["STR District"] ||
                  r?.["SDistrict"],
              ) || "",
          }))
          .filter((f) => f.firmName);

        const uniq = [];
        const seen = new Set();
        for (const f of mappedFirms) {
          const key = `${f.firmName}__${f.gst}`;
          if (!seen.has(key)) {
            seen.add(key);
            uniq.push(f);
          }
        }

        setScriptRows(rows);
        setFirms(uniq);
        setSelectedFirm(uniq.length > 0 ? uniq[0].firmName : "");

        const payload = {
          rows,
          firms: uniq,
          profile: normalizedProfile,
          loginPhone: phoneFromScript,
        };
        setFirmsCache((prev) => ({ ...prev, [storedPhone]: payload }));
        writeFirmCacheLS(storedPhone, payload);
        setHasLoadedOnce(true);
      } catch (err) {
        console.error("Failed to fetch script data:", err);
        setLoggedProfile(loggedAccount);
        setLoginPhoneFromScript("");
        setScriptRows([]);
        setFirms([]);
        setSelectedFirm("");
      } finally {
        setLoading(false);
      }
    };

    // Only hit the network if our cached firm data is older than
    // FIRMS_FRESH_MS. Otherwise the UI already has accurate data (Login
    // or a previous cart open just fetched it) and the backend's auto-
    // sync keeps the upstream cache fresh — no point in another roundtrip.
    if (cacheIsFresh) {
      console.log(
        `[cart] cache fresh (${Math.round(cacheAgeMs / 1000)}s old) — skipping network fetch`
      );
      console.timeEnd("[cart] open total");
    } else {
      fetchScriptData();
    }
  }, [open, storedPhone]); // eslint-disable-line react-hooks/exhaustive-deps

  const otpPhone = normalizePhone(loginPhoneFromScript);
  const norm = (v) =>
    String(v ?? "")
      .trim()
      .toLowerCase();
  const getItemLabel = (it) =>
    String(it?.name || it?.size || it?.standardName || "").trim();

  const detectGroup = (it) => {
    const t = norm(it?.type || it?.TYPE || it?.category || "");
    const n = norm(getItemLabel(it));
    if (t.includes("binding") || n.includes("binding")) return "binding";
    if (t.includes("ring") || n.includes("ring")) return "rings";
    if (
      t.includes("tmt") ||
      n.includes("tmt") ||
      t.includes("6mm") ||
      n.includes("6mm")
    )
      return "mm6";
    return "structure";
  };

  const grouped = useMemo(() => {
    const buckets = { structure: [], mm6: [], binding: [], rings: [] };
    for (const it of cartItems) buckets[detectGroup(it)].push(it);
    return buckets;
  }, [cartItems]);

  const hasAny = (arr) => Array.isArray(arr) && arr.length > 0;

  const tabDefs = useMemo(() => {
    const all = [
      { key: "structure", label: "Structure" },
      { key: "mm6", label: "TMT" },
      { key: "binding", label: "Binding Wire" },
      { key: "rings", label: "Rings" },
    ];
    return all.filter((t) => hasAny(grouped[t.key]));
  }, [grouped]);

  const [activeTab, setActiveTab] = useState("structure");

  useEffect(() => {
    if (!open) return;
    if (tabDefs.length === 0) {
      setActiveTab("structure");
      return;
    }
    if (!tabDefs.some((t) => t.key === activeTab)) setActiveTab(tabDefs[0].key);
  }, [tabDefs, open, activeTab]);

  const activeItems = grouped[activeTab] || [];
  const activeForm = formData[activeTab] || emptyForm;
  const activeErrors = errorsByTab[activeTab] || { vehicle: "" };

  const allTabsHavePaymentTerm = useMemo(() => {
    if (!tabDefs || tabDefs.length === 0) return false;
    return tabDefs.every((tab) => {
      const term = formData?.[tab.key]?.paymentTerms;
      return typeof term === "string" && term.trim() !== "";
    });
  }, [tabDefs, formData]);

  const getPaymentTermExtraForTerm = (term) => {
    const pt = String(term || "").toLowerCase();
    if (pt.includes("100% rtgs against proforma invoice")) return 0;
    if (pt.includes("next day rtgs")) return 0;
    if (pt.includes("ubs")) return 0;
    if (pt.includes("+300")) return 0.3;
    if (pt.includes("+450")) return 0.45;
    return 0;
  };

  const getPaymentTermExtraForGroup = (groupKey) =>
    getPaymentTermExtraForTerm(formData?.[groupKey]?.paymentTerms || "");

  const getPaymentTermExtraForItem = (item) =>
    getPaymentTermExtraForGroup(detectGroup(item));

  const getBasicRateForItem = (item) =>
    Number(item.baseRate ?? item.basicRate ?? item.rate ?? 0);

  const calcFinalRate = (item) =>
    Number(
      (getBasicRateForItem(item) + getPaymentTermExtraForItem(item)).toFixed(2),
    );

  const getBaseRateText = (item) => formatRate(getBasicRateForItem(item));
  const getPTRateText = (item) => formatRate(calcFinalRate(item));

  const groupTotals = (items) => {
    const itemsCount = items.length;
    const qty = items.reduce((s, it) => s + Number(it.qty || 0), 0);
    const total = items.reduce(
      (s, it) => s + calcFinalRate(it) * Number(it.qty || 0),
      0,
    );
    return { itemsCount, qty, total };
  };

  const activeSum = useMemo(() => groupTotals(activeItems), [activeItems]);

  // ─── live outstandings (auto-refresh every 30s while cart is open) ────────
  // Module-level "last fetched" timestamp so opening / closing / reopening
  // the cart in quick succession doesn't refetch the same data. Backend
  // auto-syncs outstandings every 30 sec anyway.
  const OUTSTANDINGS_FRESH_MS = 25_000; // re-use a fetch younger than 25s
  const lastOutstandingsAtRef = useRef(0);

  const fetchLiveOutstandings = useMemo(
    () => async () => {
      const age = Date.now() - lastOutstandingsAtRef.current;
      if (age < OUTSTANDINGS_FRESH_MS) {
        console.log(
          `[cart] outstandings cache fresh (${Math.round(age / 1000)}s) — skipping fetch`
        );
        setFetchingLive(false);
        return;
      }
      const t = `[cart] GET outstandings (${Date.now()})`;
      console.time(t);
      try {
        const res = await fetch(SHEETS.outstandings, { cache: "no-store" });
        const data = await res.json();
        const map = {};
        (data || []).forEach((item) => {
          const gst = item.GST?.trim().toUpperCase();
          if (gst) map[gst] = Number(item.Outstanding) || 0;
        });
        setLiveOutstandings(map);
        lastOutstandingsAtRef.current = Date.now();
      } catch (err) {
        console.warn("Failed to fetch live outstanding data:", err);
      } finally {
        setFetchingLive(false);
        console.timeEnd(t);
      }
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (open) fetchLiveOutstandings();
  }, [open, fetchLiveOutstandings]);

  // Poll only while the cart is open (saves bandwidth when popup is closed)
  usePolling(fetchLiveOutstandings, 30_000, open);

  useEffect(() => {
    if (!selectedFirm || firms.length === 0) {
      setFirmDetails({
        gst: "",
        contact: "",
        creditLimit: "",
        outstanding: "",
        contactPerson: "",
        area: "",
        zone: "",
        district: "",
      });
      return;
    }
    const selected = firms.find((f) => f.firmName === selectedFirm);
    if (!selected) return;
    // A firms list restored from an older cache was mapped before `zone`
    // existed, so recover it from the raw dealer rows cached alongside.
    const zoneFor = (sel) => {
      if (sel?.zone) return sel.zone;
      const gst = String(sel?.gst || "").trim().toUpperCase();
      if (!gst) return "";
      const row = (scriptRows || []).find(
        (r) =>
          String(
            r?.["GSTIN/UIN"] || r?.["GST No"] || r?.gst || r?.GST || "",
          )
            .trim()
            .toUpperCase() === gst,
      );
      return normalizeText(row?.ZONE || row?.Zone || row?.zone || "") || "";
    };
    const gstKey = String(selected.gst || "")
      .trim()
      .toUpperCase();
    const liveOutstanding =
      liveOutstandings[gstKey] !== undefined
        ? liveOutstandings[gstKey]
        : toNumberSafe(selected.outstanding);
    setFirmDetails({
      gst: selected.gst || "",
      contact: selected.contact || "",
      creditLimit: selected.creditLimit || "0",
      outstanding: liveOutstanding,
      contactPerson: selected.contactPerson || "",
      area: selected.area || "",
      zone: zoneFor(selected),
      district: selected.district || "",
    });
  }, [selectedFirm, firms, liveOutstandings, scriptRows]);

  // "Order Pending (In Kg)" for the selected firm — how much is still open
  // from earlier orders. Uses the SAME combined-orders feed the dashboard's
  // pending popup reads (the backend /pending-orders route is a stale
  // fallback and returns nothing, which is why this came through as 0).
  const [pendingKg, setPendingKg] = useState(0);
  useEffect(() => {
    const gst = String(firmDetails.gst || "").trim();
    if (!gst) {
      setPendingKg(0);
      return;
    }
    let cancelled = false;
    getPendingKg(gst)
      .then((kg) => {
        if (!cancelled) setPendingKg(kg);
      })
      .catch(() => {
        if (!cancelled) setPendingKg(0);
      });
    return () => {
      cancelled = true;
    };
  }, [firmDetails.gst]);

  useEffect(() => {
    if (timer > 0) {
      const t = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(t);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const validateVehicle = (v) => {
    if (!v.trim()) return "";
    const clean = v.replace(/[-\s]/g, "").toUpperCase();
    return !/^[A-Z]{2}[0-9]{1,2}[A-Z]{0,3}[0-9]{1,4}$/.test(clean)
      ? "Invalid format (e.g. GJ01AB1234)"
      : "";
  };

  const setTabField = (tabKey, field, value) =>
    setFormData((prev) => ({
      ...prev,
      [tabKey]: { ...(prev[tabKey] || emptyForm), [field]: value },
    }));

  const handleChange = (field, value) => {
    let formatted = value;
    if (field === "vehicle") {
      formatted = value.toUpperCase().replace(/[^A-Z0-9-]/g, "");
      const err = validateVehicle(formatted);
      setErrorsByTab((prev) => ({
        ...prev,
        [activeTab]: { ...(prev[activeTab] || {}), vehicle: err },
      }));
    }
    if (field === "driverContact")
      formatted = String(value || "")
        .replace(/\D/g, "")
        .slice(0, 10);
    setTabField(activeTab, field, formatted);
  };

  const startEditQty = (index) => {
    setEditingIndex(index);
    setEditQty(String(cartItems[index]?.qty ?? ""));
    setQtyError("");
  };

  const cancelEditQty = () => {
    setEditingIndex(null);
    setEditQty("");
    setQtyError("");
  };

  // ✅ FIXED: uses getItemStock() — no more ?? zero-trap
  const commitEditQty = (index) => {
    const raw = String(editQty).trim();
    const qtyNum = Number(raw);

    if (!raw) {
      setQtyError("Qty required");
      return;
    }
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
      setQtyError("Enter valid qty");
      return;
    }

    const item = cartItems[index];
    const availableStock = getItemStock(item);

    if (availableStock <= 0) {
      setQtyError("Required quantity is not available right now");
      return;
    }
    if (qtyNum > availableStock) {
      setQtyError("Required quantity is not available right now");
      return;
    }

    setCart((prev) =>
      prev.map((cartItem, i) => {
        if (i !== index) return cartItem;
        const finalRate = calcFinalRate(cartItem);
        return {
          ...cartItem,
          qty: qtyNum,
          subtotal: Number((finalRate * qtyNum).toFixed(2)),
          stock: availableStock,
          availableStock,
          latestStock: availableStock,
        };
      }),
    );
    cancelEditQty();
  };

  const onQtyKeyDown = (e, index) => {
    if (e.key === "Enter") commitEditQty(index);
    if (e.key === "Escape") cancelEditQty();
  };

  const removeItem = (i) =>
    setCart((prev) => prev.filter((_, idx) => idx !== i));

  // The backend builds the WhatsApp "Net Billing" lines in ₹/MT (e.g. 47000)
  // and adds the +300/+450 payment extra in ₹/MT too. A cart item's basic can
  // be either a ₹/kg rate (47) or the sheet's ₹/MT value (47000) depending on
  // what the product row carried, so normalize to ₹/MT here — otherwise a
  // per-kg value renders as "Net Billing Basic : ₹47" (1000x off).
  const toMTRate = (value) => {
    const n = Number(value || 0);
    if (!n || Number.isNaN(n)) return 0;
    return n < 500 ? Math.round(n * 1000) : Math.round(n);
  };

  // Basic / Patti Basic (₹/MT) come from the products sheet's OWN columns.
  // Deliberately NOT from the cart item's `basic` — despite the name that
  // field holds the per-kg base RATE, so it sent the item's rate (47500)
  // where the firm's Basic (47000) belongs.
  const structureProductRow = useMemo(() => {
    const first = cartItems.find((i) => detectGroup(i) === "structure");
    if (!first) return null;
    const std = normalizeText(
      first.standardName || first.name || first.size || "",
    );
    const matched =
      (std && (latestMap?.[buildKey(first.type, std)] || latestMap?.[std])) ||
      null;
    if (matched?.basic) return matched;
    // Name didn't match a product row — fall back to any row that carries a
    // Basic. Every product on the sheet shares the same Basic/Patti, and a
    // blank Basic would be worse than this.
    return (
      Object.values(latestMap || {}).find((p) => Number(p?.basic) > 0) ||
      matched
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems, latestMap]);

  const basicThousand = toMTRate(structureProductRow?.basic || 0);
  const pattiThousand = toMTRate(
    structureProductRow?.patti ||
      cartItems.find((i) => i.pattiBasic)?.pattiBasic ||
      0,
  );

  const isAnyBookingClosed = bookingClosedItems.length > 0;

  // ✅ FIXED: uses getItemStock() — no ?? zero-trap
  const isAnyStockInvalid = useMemo(() => {
    return cartItems.some((it) => {
      const qty = num(it?.qty);
      const st = getItemStock(it);
      if (!Number.isFinite(qty) || qty <= 0) return true;
      if (!Number.isFinite(st) || st <= 0) return true;
      return qty > st;
    });
  }, [cartItems]);

  const focusFirstMissingMandatory = () => {
    if (!selectedFirm) {
      scrollFocus(firmRef.current);
      return;
    }
    const firstMissingTab = (tabDefs || []).find(
      (t) => !String(formData?.[t.key]?.paymentTerms || "").trim(),
    );
    if (firstMissingTab) {
      setActiveTab(firstMissingTab.key);
      setTimeout(
        () => scrollFocus(paymentRefs.current[firstMissingTab.key]),
        80,
      );
      return;
    }
    if (!otpSent) {
      scrollFocus(otpRef.current);
      return;
    }
    if (String(otp || "").trim().length !== 4) scrollFocus(otpRef.current);
  };

  const canSubmitMandatory = useMemo(() => {
    const firmOk = !!selectedFirm && !!firmDetails.gst && !!firmDetails.contact;
    const ptOk = allTabsHavePaymentTerm;
    const otpOk = otpSent && String(otp || "").length === 4;
    const blockersOk = !isAnyBookingClosed && !isAnyStockInvalid;
    const sheetOk = sheetSyncOk && !isSyncingSheet;
    return firmOk && ptOk && otpOk && blockersOk && sheetOk;
  }, [
    selectedFirm,
    firmDetails.gst,
    firmDetails.contact,
    allTabsHavePaymentTerm,
    otpSent,
    otp,
    isAnyBookingClosed,
    isAnyStockInvalid,
    sheetSyncOk,
    isSyncingSheet,
  ]);

  // ─── Structure sheet ──────────────────────────────────────────────────────
  const fetchLatestRateStockMap = async () => {
    try {
      const res = await fetch(SHEET_URL);
      if (!res.ok) return null;
      const data = await res.json();
      const rows = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [data];

      const map = {};
      for (const r of rows) {
        const std =
          normalizeText(
            r?.["Standard Name"] ??
              r?.StandardName ??
              r?.standardName ??
              r?.Name ??
              r?.name,
          ) || "";
        if (!std) continue;

        const typeCode = normalizeText(r?.["TYPE"] ?? r?.Type ?? r?.type ?? "");
        const rateVal = pickFirst(r, [
          "Rate",
          "RATE",
          "Rtgs Rate",
          "RTGS Rate",
          "RTGS",
          "rtgs",
          "Price",
          "PRICE",
        ]);
        const stockVal = pickFirst(r, [
          "Today Stock",
          "Today Stock ",
          "TODAY STOCK",
          "TodayStock",
          "Stock",
          "STOCK",
          "Available Stock",
          "AVAILABLE",
          "Available",
          "Qty",
          "QTY",
          "Balance",
          "BALANCE",
        ]);

        // Firm's Basic / Patti Basic (₹/MT) — these are their own columns on
        // the products sheet, NOT the per-kg rate.
        const basicVal = pickFirst(r, ["Basic", "BASIC", "Basic ", "basic"]);
        const pattiVal = pickFirst(r, [
          "Patti Basic",
          "PATTI BASIC",
          "Patti Basic ",
          "PattiBasic",
          "pattiBasic",
        ]);

        const rate = num(rateVal);
        const stock = num(stockVal);
        const entry = {
          std,
          type: typeCode,
          rate,
          stock,
          basic: num(basicVal),
          patti: num(pattiVal),
        };

        const keyTyped = buildKey(typeCode, std);
        const keyPlain = std;
        map[keyTyped || keyPlain] = entry;
        if (!map[keyPlain]) map[keyPlain] = entry;
      }

      setLatestMap(map);
      setSheetFetchedAt(new Date());
      return map;
    } catch (e) {
      console.warn("fetchLatestRateStockMap failed:", e);
      return null;
    }
  };

  // ✅ NEW: fetch TMT + BW + Rings stocks from STOCK_URL
  const fetchSpecialStocksForCart = async () => {
    try {
      const res = await fetch(STOCK_URL_CART, { cache: "no-store" });
      const payload = await res.json().catch(() => []);
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

      const raw = { tmt6: {}, bw: {}, rings: {} };

      rows.forEach((row) => {
        const product = String(row?.PRPDUCT || row?.PRODUCT || "")
          .trim()
          .toLowerCase();
        const stock = Math.max(0, Number(row?.STOCK) || 0);

        if (product === "6mm") {
          const size = normSizeKeyCart(row?.SIZE);
          if (size) raw.tmt6[size] = stock;
        } else if (product === "bw") {
          const size = normSizeKeyCart(row?.SIZE);
          if (size) raw.bw[size] = stock;
        } else if (product === "ring" || product === "rings") {
          const size = normalizeRingStockKeyCart(row?.SIZE);
          if (size) raw.rings[size] = stock;
        }
      });

      const tmt6 = {
        mix: Number(
          raw.tmt6[normSizeKeyCart("Mix Size Booking")] ||
            raw.tmt6["mix size booking"] ||
            raw.tmt6["mix"] ||
            0,
        ),
        "30-40": Number(
          raw.tmt6[normSizeKeyCart("30 to 40")] ||
            raw.tmt6["30 to 40"] ||
            raw.tmt6["30-40"] ||
            0,
        ),
      };
      // Keep ALL binding-wire sizes (MS / GI / legacy 25kg), keyed by
      // normalized size, so per-item stock lookup works.
      const bw = { ...raw.bw };

      return { tmt6, bw, rings: raw.rings };
    } catch (e) {
      console.warn("fetchSpecialStocksForCart failed:", e);
      return { tmt6: { mix: 0, "30-40": 0 }, bw: { "25kg": 0 }, rings: {} };
    }
  };

  // ✅ FIXED: refreshes ALL types — structure + TMT + BW + Rings
  const refreshRateAndStockForCart = async ({ silent = false } = {}) => {
    setIsSyncingSheet(true);
    setSheetSyncOk(false);

    try {
      const [structureMap, specialStocks] = await Promise.all([
        fetchLatestRateStockMap(),
        fetchSpecialStocksForCart(),
      ]);

      if (!structureMap || typeof structureMap !== "object") {
        setIsSyncingSheet(false);
        setSheetSyncOk(false);
        if (!silent) {
          setMessageType("error");
          setMessage("Failed to sync latest rates/stock. Please try again.");
          setTimeout(() => setMessage(""), 4500);
        }
        return false;
      }

      const { tmt6: tmt6Map, bw: bwMap, rings: ringsMap } = specialStocks;

      setCart((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;

        let stockAlertLines = [];
        let rateChanged = false;
        const closed = [];

        const updated = prev.map((item) => {
          const grp = detectGroup(item);
          const qty = num(item.qty);
          const label = getItemLabel(item);

          // ── STRUCTURE ──────────────────────────────────────────────────────
          if (grp === "structure") {
            const std = getItemStdName(item);
            const typeCode = getItemTypeCode(item);
            const keyTyped = buildKey(typeCode, std);
            const keyPlain = std;
            const latest =
              (keyTyped && structureMap[keyTyped]) ||
              (keyPlain && structureMap[keyPlain]) ||
              null;

            if (!latest) return item;

            const latestRate = num(latest.rate);
            const latestStock = num(latest.stock);
            const oldBase = getBasicRateForItem(item);

            if (latestRate !== oldBase) rateChanged = true;
            if (latestRate === 0) closed.push(std);
            if (latestStock <= 0 || qty > latestStock)
              stockAlertLines.push(label);

            const extra = getPaymentTermExtraForItem(item);
            const finalRate = latestRate + extra;

            return {
              ...item,
              basic: latestRate,
              basicRate: latestRate,
              baseRate: latestRate,
              rate: Number(finalRate.toFixed(2)),
              stock: latestStock,
              availableStock: latestStock,
              latestStock, // ✅ always set all three
              subtotal: Number((qty * finalRate).toFixed(2)),
            };
          }

          // ── TMT (6MM) ──────────────────────────────────────────────────────
          if (grp === "mm6") {
            const nameNorm = norm(label);
            const isSizeRange =
              nameNorm.includes("30 to 40") || nameNorm.includes("30-40");
            const latestStock =
              Number(isSizeRange ? tmt6Map["30-40"] : tmt6Map["mix"]) || 0;

            if (latestStock <= 0 || qty > latestStock)
              stockAlertLines.push(label);

            return {
              ...item,
              stock: latestStock,
              availableStock: latestStock,
              latestStock,
            };
          }

          // ── BINDING WIRE (per size: MS / GI / legacy 25kg) ─────────────────
          if (grp === "binding") {
            const sizeLabel = label.replace(/^Binding Wire\s*-\s*/i, "").trim();
            const sizeKey = item.meta?.sizeKey || normSizeKeyCart(sizeLabel);
            const latestStock =
              Number(
                bwMap[sizeKey] ??
                  bwMap[normSizeKeyCart(sizeLabel)] ??
                  bwMap["25kg"] ??
                  0,
              ) || 0;
            if (latestStock <= 0 || qty > latestStock)
              stockAlertLines.push(label);
            return {
              ...item,
              stock: latestStock,
              availableStock: latestStock,
              latestStock,
            };
          }

          // ── RINGS ──────────────────────────────────────────────────────────
          if (grp === "rings") {
            const ringLabel = label.replace(/^Rings\s*-\s*/i, "").trim();
            const normalizedLabel = normalizeRingStockKeyCart(ringLabel);
            const matchedKey = Object.keys(ringsMap).find(
              (k) => normalizeRingStockKeyCart(k) === normalizedLabel,
            );
            // fall back to what's already on the item if not found in STOCK_URL
            const latestStock = matchedKey
              ? Number(ringsMap[matchedKey] || 0)
              : getItemStock(item);

            if (latestStock <= 0 || qty > latestStock)
              stockAlertLines.push(label);
            return {
              ...item,
              stock: latestStock,
              availableStock: latestStock,
              latestStock,
            };
          }

          return item;
        });

        setBookingClosedItems(closed);

        if (stockAlertLines.length > 0 && !silent) {
          setMessageType("error");
          setMessage("Required quantity is not available right now.");
          setTimeout(() => setMessage(""), 4500);
        } else if (rateChanged && !silent) {
          setMessageType("success");
          setMessage("Latest rates/stock synced successfully.");
          setTimeout(() => setMessage(""), 2500);
        }

        return updated;
      });

      setIsSyncingSheet(false);
      setSheetSyncOk(true);
      return true;
    } catch (err) {
      console.error("refreshRateAndStockForCart error:", err);
      setIsSyncingSheet(false);
      setSheetSyncOk(false);
      if (!silent) {
        setMessageType("error");
        setMessage("Failed to sync latest rates/stock. Please try again.");
        setTimeout(() => setMessage(""), 4500);
      }
      return false;
    }
  };

  useEffect(() => {
    if (!open) return;
    refreshRateAndStockForCart({ silent: true });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    setCart((prev) =>
      prev.map((item) => {
        const qty = Number(item.qty || 0);
        const finalRate = calcFinalRate(item);
        return {
          ...item,
          finalRate,
          subtotal: Number((finalRate * qty).toFixed(2)),
        };
      }),
    );
  }, [open, formData]); // eslint-disable-line react-hooks/exhaustive-deps

  const getPaymentTermLabel = (term) => {
    if (!term) return "";
    const t = String(term).toLowerCase();
    if (t.includes("next day")) return "(ND)";
    if (t.includes("10 days")) return "(10 Days)";
    if (t.includes("15 days")) return "(15 Days)";
    if (t.includes("100%")) return "(100% RTGS)";
    if (t.includes("ubs")) return "(ND)";
    return "";
  };

  // ─── OTP ──────────────────────────────────────────────────────────────────
  const handleSendOtp = async () => {
    setSubmitAttempted(true);
    if (!allTabsHavePaymentTerm) {
      focusFirstMissingMandatory();
      setMessageType("error");
      setMessage("Please select Payment Term for all tabs/items.");
      setTimeout(() => setMessage(""), 3000);
      return;
    }
    if (isAnyBookingClosed) {
      setMessageType("error");
      setMessage(
        "Booking closed for some items (rate is 0). Please remove/adjust.",
      );
      setTimeout(() => setMessage(""), 5000);
      return;
    }
    if (isAnyStockInvalid) {
      setMessageType("error");
      setMessage("Required quantity is not available right now");
      setTimeout(() => setMessage(""), 5000);
      return;
    }
    if (!sheetSyncOk || isSyncingSheet) {
      setMessageType("error");
      setMessage("Syncing latest rates… please wait.");
      setTimeout(() => setMessage(""), 2500);
      return;
    }
    if (!otpPhone || otpPhone.length !== 10) {
      setMessageType("error");
      setMessage("Login phone number not found from login details!");
      return;
    }
    if (DEMO_OTP_ENABLED && otpPhone === DEMO_PHONE) {
      setOtpSent(true);
      setTimer(180);
      setCanResend(false);
      setDemoOtpValue(DEMO_OTP);
      setOtp(DEMO_OTP);
      setMessageType("success");
      setMessage(`DEMO OTP shown on screen for ${DEMO_PHONE}.`);
      setTimeout(() => setMessage(""), 5000);
      return;
    }
    setDemoOtpValue("");
    setMessageType("success");
    setMessage(`Sending OTP on WhatsApp to ${otpPhone}...`);
    try {
      const res = await fetch(SEND_OTP_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: otpPhone }),
      });
      const data = await res.json();
      if (data.success) {
        setOtpSent(true);
        setTimer(180);
        setCanResend(false);
        setOtp("");
        setMessageType("success");
        setMessage("OTP sent successfully on WhatsApp!");
        setTimeout(() => setMessage(""), 5000);
      } else {
        setMessageType("error");
        setMessage(data.message || "Failed to send OTP");
      }
    } catch (err) {
      setMessageType("error");
      setMessage("Network error");
      console.error(err);
    }
  };

  const normalizeSubmitPaymentTerm = (term) => {
    if (
      String(term || "")
        .trim()
        .toLowerCase() === "ubs"
    )
      return "Next Day RTGS";
    return term || "";
  };

  const normalizeSubmitRemarks = (remarks, term) => {
    const base = String(remarks || "").trim();
    const pt = String(term || "")
      .trim()
      .toLowerCase();
    if (pt === "ubs") {
      if (!base) return "UBS";
      if (!base.toLowerCase().includes("ubs")) return `${base} | UBS`;
    }
    return base;
  };

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmitOrder = async () => {
    setSubmitAttempted(true);
    if (
      !allTabsHavePaymentTerm ||
      !selectedFirm ||
      !otpSent ||
      String(otp || "").length !== 4
    )
      focusFirstMissingMandatory();

    if (!allTabsHavePaymentTerm) {
      setMessageType("error");
      setMessage("Select Payment Term for all tabs before submitting.");
      setTimeout(() => setMessage(""), 3000);
      return;
    }
    if (isAnyBookingClosed) {
      setMessageType("error");
      setMessage(
        "Cannot submit. Booking is closed for some items (rate is 0).",
      );
      setTimeout(() => setMessage(""), 5000);
      return;
    }
    if (isAnyStockInvalid) {
      setMessageType("error");
      setMessage("Required quantity is not available right now.");
      setTimeout(() => setMessage(""), 5000);
      return;
    }

    setMessageType("success");
    setMessage("Syncing latest rate/stock…");
    const okSync = await refreshRateAndStockForCart({ silent: true });

    if (!okSync) {
      setMessageType("error");
      setMessage("Latest rates/stock sync failed. Please try again.");
      setTimeout(() => setMessage(""), 4500);
      return;
    }
    if (bookingClosedItems.length > 0) {
      setMessageType("error");
      setMessage(
        "Rates updated: booking is closed for some items now. Please remove them.",
      );
      setTimeout(() => setMessage(""), 6000);
      return;
    }

    const isOtpVerified = otpSent && String(otp || "").trim().length === 4;
    if (!canSubmitMandatory || !isOtpVerified) {
      focusFirstMissingMandatory();
      setMessageType("error");
      setMessage("Please fill all mandatory fields and verify OTP.");
      setTimeout(() => setMessage(""), 2500);
      return;
    }

    // Save the order-form free-text values to localStorage history so they
    // appear as suggestions on the next order. We save on submit (not on
    // every keystroke) so half-typed garbage doesn't pollute the dropdown.
    // Each tab can have its own values — capture every unique entry
    // across all tabs being submitted.
    try {
      for (const tab of tabDefs || []) {
        const f = formData?.[tab.key];
        if (!f) continue;
        writeHistoryEntry(storedPhone, "vehicle", f.vehicle);
        writeHistoryEntry(storedPhone, "deliveryArea", f.deliveryArea);
        writeHistoryEntry(storedPhone, "driverContact", f.driverContact);
        writeHistoryEntry(storedPhone, "brokerName", f.brokerName);
      }
    } catch {
      /* non-critical */
    }

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    const submitToGoogleForm = async (FORM_URL, params) => {
      try {
        await fetch(FORM_URL, {
          method: "POST",
          mode: "no-cors",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: params.toString(),
        });
      } catch (e) {
        console.warn("Google Form submit failed:", e);
      }
    };

    // Write structure rows into the orders sheet. `text/plain` keeps this a
    // CORS simple request (no preflight — Apps Script rejects preflight) while
    // still letting us READ the reply, so a real failure can be retried.
    // The sheet dedupes on requestId, so retries never double-write.
    const submitStructureToSheet = async (requestId, rows) => {
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const res = await fetch(STRUCTURE_SHEET_URL, {
            method: "POST",
            headers: { "Content-Type": "text/plain;charset=utf-8" },
            body: JSON.stringify({ requestId, rows }),
            redirect: "follow",
          });
          const data = await res.json().catch(() => null);
          if (data?.success) return true;
          console.warn("Orders sheet rejected submit:", data);
        } catch (e) {
          console.warn(`Orders sheet submit attempt ${attempt} failed:`, e);
        }
        if (attempt < 3) await sleep(attempt * 800);
      }
      return false;
    };

    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      setMessageType("success");
      setMessage("Submitting order...");

      const cartItemsPayload = cartItems.map((item) => {
        const qty = Number(item.qty || 0);
        const grp = detectGroup(item);
        const tabForm = formData?.[grp] || emptyForm;
        const paymentTerms = normalizeSubmitPaymentTerm(tabForm.paymentTerms);
        const remarks = normalizeSubmitRemarks(
          tabForm.remarks,
          tabForm.paymentTerms,
        );
        const currentBaseRate = Number(
          item.baseRate ?? item.basicRate ?? item.rate ?? 0,
        );
        const extra = getPaymentTermExtraForTerm(tabForm.paymentTerms);
        const finalRate = currentBaseRate + extra;
        return {
          ...item,
          group: grp,
          tabForm: { ...tabForm, paymentTerms, remarks },
          qty,
          basicRate: Number(currentBaseRate.toFixed(2)),
          baseRate: Number(currentBaseRate.toFixed(2)),
          finalRate: Number(finalRate.toFixed(2)),
          rate: Number(finalRate.toFixed(2)),
          subtotal: Number((finalRate * qty).toFixed(2)),
        };
      });

      const finalTotalQty = cartItemsPayload.reduce((s, i) => s + i.qty, 0);
      const finalTotalAmount = cartItemsPayload.reduce(
        (s, i) => s + Number(i.subtotal || 0),
        0,
      );

      const getFirstGroupItem = (g) =>
        cartItemsPayload.find((i) => i.group === g) || null;
      const getGroupBaseRate = (g) =>
        Number(
          getFirstGroupItem(g)?.baseRate ??
            getFirstGroupItem(g)?.basicRate ??
            0,
        );
      const getGroupFinalRate = (g) =>
        Number(
          getFirstGroupItem(g)?.finalRate ?? getFirstGroupItem(g)?.rate ?? 0,
        );

      const structurePaymentExtra = getPaymentTermExtraForTerm(
        formData.structure.paymentTerms,
      );
      const structureBasicRateMT = Number(basicThousand || 0);
      const structureNetBillingMT =
        structureBasicRateMT + Number(structurePaymentExtra || 0);

      const orderPayload = {
        cartItems: cartItemsPayload,
        totalQty: finalTotalQty,
        totalAmount: finalTotalAmount,
        selectedFirm,
        firmDetails,
        otpVerified: isOtpVerified,
        formDataByTab: {
          structure: {
            ...formData.structure,
            paymentTerms: normalizeSubmitPaymentTerm(
              formData.structure.paymentTerms,
            ),
            remarks: normalizeSubmitRemarks(
              formData.structure.remarks,
              formData.structure.paymentTerms,
            ),
            basicRateMT: structureBasicRateMT,
            netBillingMT: structureNetBillingMT,
          },
          mm6: {
            ...formData.mm6,
            paymentTerms: normalizeSubmitPaymentTerm(formData.mm6.paymentTerms),
            remarks: normalizeSubmitRemarks(
              formData.mm6.remarks,
              formData.mm6.paymentTerms,
            ),
            basicRateMT: toThousandRate(getGroupBaseRate("mm6")),
            netBillingMT: toThousandRate(getGroupFinalRate("mm6")),
          },
          binding: {
            ...formData.binding,
            paymentTerms: normalizeSubmitPaymentTerm(
              formData.binding.paymentTerms,
            ),
            remarks: normalizeSubmitRemarks(
              formData.binding.remarks,
              formData.binding.paymentTerms,
            ),
            basicRateMT: toThousandRate(getGroupBaseRate("binding")),
            netBillingMT: toThousandRate(getGroupFinalRate("binding")),
          },
          rings: {
            ...formData.rings,
            paymentTerms: normalizeSubmitPaymentTerm(
              formData.rings.paymentTerms,
            ),
            remarks: normalizeSubmitRemarks(
              formData.rings.remarks,
              formData.rings.paymentTerms,
            ),
            basicRateMT: toThousandRate(getGroupBaseRate("rings")),
            netBillingMT: toThousandRate(getGroupFinalRate("rings")),
          },
        },
        loggedDealer: loggedProfile || loggedAccount,
        basicThousand,
        pattiThousand,
      };

      const res = await fetch(`${API_BASE}/api/submit-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderPayload),
      });
      const data = await res.json();

      if (!data.success) {
        setMessageType("error");
        setMessage("Failed: " + (data.message || "Unknown error"));
        return;
      }

      const requestIdsFromBackend = data.requestIds || {};
      const fallbackSingleId = data.requestId || "";
      const mergedIds = {
        structure: requestIdsFromBackend.structure || "",
        mm6: requestIdsFromBackend.mm6 || "",
        binding: requestIdsFromBackend.binding || "",
        rings: requestIdsFromBackend.rings || "",
      };
      if (!mergedIds.structure && fallbackSingleId)
        mergedIds.structure = fallbackSingleId;

      setSuccessRequestIds(mergedIds);
      setSuccessOpen(true);
      setOtp("");
      setOtpSent(false);
      setTimer(60);
      setCanResend(true);

      const today = new Date().toISOString().split("T")[0];
      const dealerData = loggedProfile || loggedAccount || {};
      const userName =
        normalizeText(
          dealerData?.["Particulars"] ||
            dealerData?.["Firm Name"] ||
            dealerData?.name ||
            dealerData?.username,
        ) ||
        selectedFirm ||
        "";
      const userId =
        normalizeText(
          dealerData?.phone || dealerData?.userid || dealerData?.userId,
        ) ||
        storedPhone ||
        "";

      // Structure → orders sheet (one row per item). Payment extra is ₹/MT
      // here, matching Basic/Patti which are ₹/MT.
      const structureExtraMT = Math.round(
        getPaymentTermExtraForTerm(formData.structure.paymentTerms) * 1000,
      );
      const buildStructureSheetRows = () =>
        cartItemsPayload
          .filter((i) => i.group === "structure")
          .map((item) => {
            const f = item.tabForm || emptyForm;
            return {
              userName,
              userNumber: userId,
              basic: basicThousand,
              patti: pattiThousand,
              sd: 0,
              netBasic: basicThousand ? basicThousand + structureExtraMT : "",
              netPatti: pattiThousand ? pattiThousand + structureExtraMT : "",
              firmName: selectedFirm,
              gst: firmDetails.gst || "",
              area: firmDetails.area || "",
              district: getDistrictValue(),
              zone: firmDetails.zone || "",
              contactPerson: firmDetails.contactPerson || "",
              contactNumber: firmDetails.contact || "",
              pending: pendingKg,
              through: f.brokerName || "",
              executive: "DIRECTFROMCLIENT", // orders placed by the dealer themselves
              remarks: f.remarks || "",
              plannedDate: f.loadingDate || today,
              vehicleNumber: f.vehicle || "",
              driverNumber: f.driverContact || "",
              paymentTerms: f.paymentTerms || "",
              creditLimit: String(firmDetails.creditLimit || "0").replace(
                /,/g,
                "",
              ),
              outstanding: String(firmDetails.outstanding || "0"),
              size: item.size || item.name || item.standardName || "",
              qty: Number(item.qty || 0),
              // Sheet's Rate column is ₹/MT (47500), not the ₹/kg the cart
              // shows (47.50) — same unit as Basic/Patti/Net Basic.
              rate: toMTRate(Number(item.finalRate ?? item.rate ?? 0)),
              deliveryArea: f.deliveryArea || "",
            };
          });

      const qtyByGroup = {
        structure: cartItemsPayload
          .filter((i) => i.group === "structure")
          .reduce((s, i) => s + Number(i.qty || 0), 0),
        mm6: cartItemsPayload
          .filter((i) => i.group === "mm6")
          .reduce((s, i) => s + Number(i.qty || 0), 0),
        binding: cartItemsPayload
          .filter((i) => i.group === "binding")
          .reduce((s, i) => s + Number(i.qty || 0), 0),
        rings: cartItemsPayload
          .filter((i) => i.group === "rings")
          .reduce((s, i) => s + Number(i.qty || 0), 0),
      };

      const groupIndexMap = { structure: [], mm6: [], binding: [], rings: [] };
      cartItemsPayload.forEach((it, idx) => groupIndexMap[it.group]?.push(idx));

      const groupCounters = { structure: 0, mm6: 0, binding: 0, rings: 0 };
      const groupLastIndex = {
        structure: groupIndexMap.structure.at(-1) ?? -1,
        mm6: groupIndexMap.mm6.at(-1) ?? -1,
        binding: groupIndexMap.binding.at(-1) ?? -1,
        rings: groupIndexMap.rings.at(-1) ?? -1,
      };

      const netBillingBasicStructure = cartItemsPayload
        .filter((i) => i.group === "structure")
        .reduce(
          (sum, i) => sum + Number(i.finalRate || 0) * Number(i.qty || 0),
          0,
        );

      // Structure also goes to the orders sheet — one post for the whole
      // order, alongside the Google Form below (the form still drives the
      // plant WhatsApp message / Approved status / dispatch no.).
      // Non-blocking: the form is the safety net, so a sheet hiccup must not
      // stop the dealer's order. Once the form is retired, make this blocking.
      const structureRows = STRUCTURE_SHEET_URL ? buildStructureSheetRows() : [];
      if (structureRows.length) {
        const structureRequestId =
          requestIdsFromBackend?.structure ||
          mergedIds?.structure ||
          fallbackSingleId;
        submitStructureToSheet(structureRequestId, structureRows).then((ok) => {
          if (!ok) {
            console.error(
              "Orders sheet write FAILED after retries for",
              structureRequestId,
              "— order still recorded via Google Form.",
            );
          }
        });
      }

      for (let index = 0; index < cartItemsPayload.length; index++) {
        const item = cartItemsPayload[index];
        const grp = item.group;
        const FORM_URL = FORM_URLS[grp] || FORM_URLS.structure;

        if (grp === "binding" && index !== groupLastIndex.binding) continue;

        const groupRequestId =
          requestIdsFromBackend?.[grp] || mergedIds?.[grp] || fallbackSingleId;
        groupCounters[grp] = (groupCounters[grp] || 0) + 1;
        const serialNo =
          index === groupLastIndex[grp] ? "final" : String(groupCounters[grp]);

        const itemBaseRate = Number(
          item.baseRate ?? item.basicRate ?? item.rate ?? 0,
        );
        const itemFinalRate = Number(item.finalRate ?? item.rate ?? 0);
        const f = item.tabForm || emptyForm;
        const plant =
          item.plant ||
          item.Plant ||
          item["Plant"] ||
          item.plantName ||
          item["Plant Name"] ||
          "";

        let params;

        if (grp === "structure") {
          params = new URLSearchParams({
            "entry.1057610496": groupRequestId,
            "entry.87964108": serialNo,
            "entry.65281072": selectedFirm,
            "entry.480972465": firmDetails.gst || "",
            "entry.1937377893": firmDetails.contactPerson || "",
            "entry.181930812": firmDetails.contact || "",
            "entry.1616874208": String(firmDetails.creditLimit || "0").replace(
              /,/g,
              "",
            ),
            "entry.1726420592": String(firmDetails.outstanding || "0"),
            "entry.1844245338": f.vehicle || "",
            "entry.115808162": f.deliveryArea || "",
            "entry.1093762713": f.driverContact || "",
            "entry.405211172": f.loadingDate || today,
            "entry.1998166734": f.paymentTerms || "",
            "entry.596191894": f.remarks || "",
            "entry.364531829": f.brokerName || "",
            "entry.58254398": item.size || item.name || item.standardName || "",
            "entry.2065614878": String(itemFinalRate.toFixed(2)),
            "entry.1913924093": String(item.qty),
          });
          if (index === groupLastIndex.structure)
            params.append(
              "entry.987654321",
              netBillingBasicStructure.toFixed(2),
            );
        } else if (grp === "mm6") {
          const FIXED_PLANT = "Bavla Unit";
          const districtFromDealer = getDistrictValue();
          const finalMarker =
            index === groupLastIndex.mm6 ? "Final" : String(groupCounters[grp]);
          const mm6NetBilling = String(toThousandRate(itemFinalRate));
          params = new URLSearchParams({
            "entry.1825729079": groupRequestId,
            "entry.261191874": userName,
            "entry.559407481": userId,
            "entry.447271554": selectedFirm,
            "entry.785499247": firmDetails.gst || "",
            "entry.1857632980": firmDetails.area || "",
            "entry.1597407930": districtFromDealer,
            "entry.1820794487": firmDetails.contactPerson || "",
            "entry.1523703513": firmDetails.contact || "",
            "entry.1554509189": "",
            "entry.769131076": String(firmDetails.creditLimit || "0").replace(
              /,/g,
              "",
            ),
            "entry.293299877": String(firmDetails.outstanding || "0"),
            "entry.1526526587": String(toThousandRate(itemBaseRate)),
            "entry.1702401852": "",
            "entry.545541904": mm6NetBilling,
            "entry.1937721093": "0",
            "entry.909433954": f.vehicle || "",
            "entry.1402715628": f.deliveryArea || "",
            "entry.1593061983": f.driverContact || "",
            "entry.1066548477": f.loadingDate || today,
            "entry.381364910": f.paymentTerms || "",
            "entry.1303915694": f.remarks || "",
            "entry.2028227369": getMm6SizeLabel(item),
            "entry.901209758": mm6NetBilling,
            [MM6_STATUS_ENTRY]: "pending",
            "entry.581146885": String(item.qty),
            "entry.351745792": "",
            "entry.663568576": "",
            "entry.476834624": "",
            "entry.1019688823": "",
            "entry.2110319180": "",
            "entry.979567379": "",
            "entry.1932005240": "",
            "entry.260364162": "",
            "entry.837833275": "",
            "entry.1838765810": FIXED_PLANT || plant || "",
          });
          if (MM6_FINAL_ENTRY) params.append(MM6_FINAL_ENTRY, finalMarker);
        } else if (grp === "binding") {
          const FIXED_PLANT = "Santej wire Unit";
          const districtFromDealer = getDistrictValue();
          const bindingTotalQty = String(qtyByGroup.binding || 0);
          const bindingNetBilling = String(toThousandRate(itemFinalRate));
          params = new URLSearchParams({
            "entry.1825729079": groupRequestId,
            "entry.712605167": userName,
            "entry.856984330": userId,
            "entry.890144154": selectedFirm,
            "entry.1177662709": firmDetails.gst || "",
            "entry.27099454": firmDetails.area || "",
            "entry.963123383": districtFromDealer,
            "entry.240497007": firmDetails.contactPerson || "",
            "entry.801934692": firmDetails.contact || "",
            "entry.2144449733": "",
            "entry.1111887342": String(firmDetails.outstanding || "0"),
            "entry.1160074286": String(firmDetails.creditLimit || "0").replace(
              /,/g,
              "",
            ),
            "entry.1559381514": String(toThousandRate(itemBaseRate)),
            "entry.65758271": "",
            "entry.824365398": f.paymentTerms || "",
            "entry.1943714361": bindingNetBilling,
            "entry.1414313346": "",
            "entry.1533711379": f.deliveryArea || "",
            "entry.976289867": bindingTotalQty,
            "entry.1999859032": f.remarks || "",
            "entry.948231335": getBindingSizeLabel(item),
            "entry.29771064": bindingTotalQty,
            "entry.294882769": bindingNetBilling,
            [BINDING_STATUS_ENTRY]: "pending",
            "entry.2088343950": FIXED_PLANT || plant || "",
            "entry.1156020113": "",
            "entry.798107039": "",
            "entry.196242017": "",
            "entry.1889019449": "",
          });
          if (BINDING_FINAL_ENTRY) params.append(BINDING_FINAL_ENTRY, "Final");
        } else if (grp === "rings") {
          const finalMarker =
            index === groupLastIndex.rings
              ? "Final"
              : String(groupCounters[grp]);
          params = new URLSearchParams({
            "entry.1825729079": groupRequestId,
            "entry.1692857377":
              normalizeText(dealerData?.type || dealerData?.Type) || "Dealer",
            "entry.605106569": userName,
            "entry.20146904": userId,
            "entry.681496635": String(toThousandRate(itemBaseRate)),
            "entry.1987200670": f.paymentTerms || "",
            "entry.1123848596": "",
            "entry.544968277": "",
            "entry.656204627": selectedFirm,
            "entry.1828518522": firmDetails.gst || "",
            "entry.179561114": firmDetails.area || "",
            "entry.1275424782": "",
            "entry.256189815": "",
            "entry.1382679828": firmDetails.contactPerson || "",
            "entry.1678187843": firmDetails.contact || "",
            "entry.418753885": selectedFirm,
            "entry.1686218155": firmDetails.gst || "",
            "entry.310864496": cleanSizeName(
              item.size || item.name || item.standardName || "",
            ),
            "entry.1599379406": String(item.qty),
            "entry.971756679": "",
            "entry.562498507": String(toThousandRate(itemFinalRate)),
            "entry.808999266": firmDetails.area || "",
            [RINGS_STATUS_ENTRY]: "pending",
          });
          if (RINGS_FINAL_ENTRY) params.append(RINGS_FINAL_ENTRY, finalMarker);
        }

        await submitToGoogleForm(FORM_URL, params);
        await sleep(250);
      }

      setTimeout(() => {
        setCart([]);
        setSelectedFirm("");
        setFirmDetails({
          gst: "",
          contact: "",
          creditLimit: "",
          outstanding: "",
          contactPerson: "",
          area: "",
          district: "",
        });
        setFormData({
          structure: { ...emptyForm },
          mm6: { ...emptyForm },
          binding: { ...emptyForm },
          rings: { ...emptyForm },
        });
        // Order placed — wipe the persisted form so the next order isn't
        // auto-populated with last order's values. (Autocomplete history
        // is preserved separately so suggestions still work.)
        try {
          localStorage.removeItem(formDataLsKey);
        } catch {
          /* non-critical */
        }
        setErrorsByTab({
          structure: { vehicle: "" },
          mm6: { vehicle: "" },
          binding: { vehicle: "" },
          rings: { vehicle: "" },
        });
        setOtp("");
        setMessage("");
        setSuccessOpen(false);
        setIsSubmitting(false);
        onClose();
        navigate("/dashboard");
      }, 1800);
    } catch (err) {
      console.error(err);
      setMessageType("error");
      setMessage("Network error. Try again.");
      setIsSubmitting(false);
    }
  };

  const dealerData = loggedProfile || loggedAccount || {};
  const ubsStatus = String(dealerData?.["UBS STATUS"] || "").trim();

  if (!open) return null;

  const numericCreditLimit =
    Number(String(firmDetails.creditLimit ?? "0").replace(/,/g, "")) || 0;
  const numericOutstanding = Number(firmDetails.outstanding || 0) || 0;
  const availableLimit = Math.max(numericCreditLimit - numericOutstanding, 0);

  // ─── cart section ─────────────────────────────────────────────────────────
  const renderActiveCartSection = () => {
    if (!hasAny(activeItems)) {
      return (
        <div className="bg-gray-50 rounded-2xl p-10 text-center text-gray-500 border">
          No items in{" "}
          <span className="font-bold">
            {tabDefs.find((t) => t.key === activeTab)?.label}
          </span>
        </div>
      );
    }

    return (
      <div className="rounded-2xl border bg-white overflow-hidden shadow-sm">
        <div className="px-4 sm:px-5 py-3 border-b bg-gray-50 flex items-center justify-between">
          <div className="font-extrabold text-gray-800">
            {tabDefs.find((t) => t.key === activeTab)?.label} Items
          </div>
          <div className="text-xs sm:text-sm font-semibold text-gray-600">
            {activeSum.itemsCount} item(s) •{" "}
            {activeSum.qty.toLocaleString("en-IN")} Kg
          </div>
        </div>

        <div className="hidden sm:grid grid-cols-12 gap-2 px-5 py-2 text-xs font-bold text-gray-600 border-b">
          <div className="col-span-5">Size</div>
          <div className="col-span-2">Rate</div>
          <div className="col-span-2 text-right">Qty</div>
          <div className="col-span-2 text-right">Amount</div>
          <div className="col-span-1 text-right"></div>
        </div>

        <div className="px-3 sm:px-5 py-3 space-y-2 sm:space-y-3 overflow-y-auto max-h-[260px] sm:max-h-[360px]">
          {activeItems.map((item) => {
            const globalIndex = cartItems.indexOf(item);
            const isEditing = editingIndex === globalIndex;
            const finalRate = calcFinalRate(item);
            const isClosed = Number(getBasicRateForItem(item) || 0) === 0;

            // ✅ FIXED: getItemStock() — no more ?? zero-trap for any type
            const st = getItemStock(item);
            const qty = num(item.qty);
            const stockBad = Number.isFinite(st) && (st <= 0 || qty > st);
            const stockText = stockBad
              ? "Required quantity is not available right now"
              : "";

            return (
              <div
                key={`${activeTab}-${globalIndex}`}
                className="border-b last:border-0 pb-2 sm:pb-0"
              >
                {/* MOBILE */}
                <div className="sm:hidden bg-white rounded-2xl p-3 border">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex flex-col">
                      <p className="font-semibold text-gray-800 text-sm leading-snug line-clamp-2">
                        {cleanSizeName(getItemLabel(item))}
                      </p>
                      {isClosed && (
                        <span className="mt-1 text-xs font-extrabold text-red-600">
                          Booking Closed (Rate 0)
                        </span>
                      )}
                      {stockText && (
                        <span className="mt-1 text-xs font-extrabold text-red-600">
                          {stockText}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      {!isEditing ? (
                        <button
                          onClick={() => startEditQty(globalIndex)}
                          className="text-gray-700 hover:text-yellow-700"
                          type="button"
                        >
                          <EditIcon sx={{ fontSize: 20 }} />
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => commitEditQty(globalIndex)}
                            className="text-green-700 hover:text-green-800"
                            type="button"
                          >
                            <CheckIcon sx={{ fontSize: 20 }} />
                          </button>
                          <button
                            onClick={cancelEditQty}
                            className="text-gray-600 hover:text-gray-800"
                            type="button"
                          >
                            <CloseRoundedIcon sx={{ fontSize: 20 }} />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => removeItem(globalIndex)}
                        className="text-red-600 hover:text-red-800"
                        type="button"
                      >
                        <DeleteIcon sx={{ fontSize: 20 }} />
                      </button>
                    </div>
                  </div>

                  <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-gray-700">
                    <div className="flex flex-col">
                      <span className="text-gray-500 font-semibold">Rate</span>
                      <span className="font-bold">
                        ₹{getBaseRateText(item)}/kg
                      </span>
                      <span className="text-xs font-bold text-green-700">
                        ₹{getPTRateText(item)}/kg{" "}
                        {getPaymentTermLabel(activeForm.paymentTerms)}
                      </span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-gray-500 font-semibold">Qty</span>
                      {!isEditing ? (
                        <span className="font-bold">
                          {Number(item.qty || 0).toLocaleString("en-IN")} Kg
                        </span>
                      ) : (
                        <div className="w-full">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={editQty}
                            onChange={(e) =>
                              setEditQty(e.target.value.replace(/[^\d]/g, ""))
                            }
                            onKeyDown={(e) => onQtyKeyDown(e, globalIndex)}
                            className="w-full text-center px-2 py-1 rounded-lg border border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-200 font-bold"
                            placeholder="Qty"
                          />
                          {qtyError && (
                            <p className="text-[10px] text-red-600 mt-1 text-center">
                              {qtyError}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-gray-500 font-semibold">
                        Amount
                      </span>
                      <span className="font-bold text-gray-900">
                        ₹
                        {(finalRate * Number(item.qty || 0)).toLocaleString(
                          "en-IN",
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* DESKTOP */}
                <div className="hidden sm:grid grid-cols-12 gap-2 py-3 text-sm items-center">
                  <div className="col-span-5 font-medium truncate pr-2">
                    <div className="flex flex-col">
                      <span>{cleanSizeName(getItemLabel(item))}</span>
                      {isClosed && (
                        <span className="text-xs font-extrabold text-red-600">
                          Booking Closed (Rate 0)
                        </span>
                      )}
                      {stockText && (
                        <span className="text-xs font-extrabold text-red-600">
                          {stockText}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="col-span-2 leading-tight">
                    <div>₹{getBaseRateText(item)}/kg</div>
                    <span className="text-xs font-bold text-green-700">
                      ₹{getPTRateText(item)}/kg{" "}
                      {getPaymentTermLabel(activeForm.paymentTerms)}
                    </span>
                  </div>
                  <div className="col-span-2 text-right">
                    {!isEditing ? (
                      <span className="font-semibold">
                        {Number(item.qty || 0).toLocaleString("en-IN")} Kg
                      </span>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={editQty}
                          onChange={(e) =>
                            setEditQty(e.target.value.replace(/[^\d]/g, ""))
                          }
                          onKeyDown={(e) => onQtyKeyDown(e, globalIndex)}
                          className="w-28 text-right px-3 py-1 rounded-lg border border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-200 font-bold"
                          placeholder="Qty"
                        />
                        <button
                          onClick={() => commitEditQty(globalIndex)}
                          className="text-green-700 hover:text-green-800"
                          title="Save"
                          type="button"
                        >
                          <CheckIcon sx={{ fontSize: 22 }} />
                        </button>
                        <button
                          onClick={cancelEditQty}
                          className="text-gray-600 hover:text-gray-800"
                          title="Cancel"
                          type="button"
                        >
                          <CloseRoundedIcon sx={{ fontSize: 22 }} />
                        </button>
                      </div>
                    )}
                    {isEditing && qtyError && (
                      <p className="text-xs text-red-600 mt-1">{qtyError}</p>
                    )}
                  </div>
                  <div className="col-span-2 text-right font-bold">
                    ₹
                    {(finalRate * Number(item.qty || 0)).toLocaleString(
                      "en-IN",
                    )}
                  </div>
                  <div className="col-span-1 text-right">
                    {!isEditing ? (
                      <button
                        onClick={() => startEditQty(globalIndex)}
                        className="text-gray-700 hover:text-yellow-700"
                        title="Edit qty"
                        type="button"
                      >
                        <EditIcon sx={{ fontSize: 22 }} />
                      </button>
                    ) : null}{" "}
                    <button
                      onClick={() => removeItem(globalIndex)}
                      className="text-red-600 hover:text-red-800 ml-2"
                      title="Remove"
                      type="button"
                    >
                      <DeleteIcon sx={{ fontSize: 22 }} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-4 sm:px-5 py-3 border-t bg-gray-50">
          <div className="grid grid-cols-2 gap-2 text-sm font-semibold text-gray-700">
            <div>
              <div className="text-xs text-gray-500 font-bold">
                Payment Term
              </div>
              <div className="font-extrabold text-gray-900">
                {activeForm.paymentTerms || "Not selected"}{" "}
                <span className="text-xs font-bold text-gray-600">
                  {getPaymentTermLabel(activeForm.paymentTerms)}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500 font-bold">Total</div>
              <div className="font-extrabold text-gray-900">
                ₹{activeSum.total.toLocaleString("en-IN")}
              </div>
              <div className="text-xs font-bold text-gray-600">
                Qty: {activeSum.qty.toLocaleString("en-IN")} Kg
              </div>
            </div>
          </div>
          {activeTab === "structure" && (
            <div className="mt-2 text-xs font-semibold text-gray-600 text-right space-y-1">
              <div>Plus: Loading @350 PMT</div>
              <div>Plus: GST</div>
            </div>
          )}
          {activeTab === "mm6" && (
            <div className="mt-2 text-xs font-semibold text-gray-600 text-right space-y-1">
              <div>Plus: Loading @350 PMT</div>
              <div>Plus: GST</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <>
      <AnimatePresence>
        {successOpen && (
          <motion.div
            className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6"
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
            >
              <div className="text-center">
                <div className="text-3xl">✅</div>
                <h3 className="mt-2 text-xl font-extrabold text-gray-900">
                  Order Submitted Successfully!
                </h3>
                <p className="mt-2 text-xs text-gray-500">
                  You will be redirected to dashboard…
                </p>
              </div>
              <div className="mt-4 rounded-2xl border bg-gray-50 p-4 space-y-2 text-sm">
                {successRequestIds.structure && (
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-gray-700">Structure</span>
                    <span className="font-extrabold text-gray-900">
                      {successRequestIds.structure}
                    </span>
                  </div>
                )}
                {successRequestIds.mm6 && (
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-gray-700">6mm / TMT</span>
                    <span className="font-extrabold text-gray-900">
                      {successRequestIds.mm6}
                    </span>
                  </div>
                )}
                {successRequestIds.binding && (
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-gray-700">
                      Binding Wire
                    </span>
                    <span className="font-extrabold text-gray-900">
                      {successRequestIds.binding}
                    </span>
                  </div>
                )}
                {successRequestIds.rings && (
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-gray-700">Rings</span>
                    <span className="font-extrabold text-gray-900">
                      {successRequestIds.rings}
                    </span>
                  </div>
                )}
                {!successRequestIds.structure &&
                  !successRequestIds.mm6 &&
                  !successRequestIds.binding &&
                  !successRequestIds.rings && (
                    <div className="text-center text-gray-600 font-bold">
                      Request IDs not available
                    </div>
                  )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl flex flex-col my-4"
          style={{ maxHeight: "94vh" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-white rounded-t-3xl border-b border-gray-200 px-4 sm:px-8 py-3 shadow-sm">
            <div className="flex justify-between items-center">
              <h2 className="text-lg sm:text-2xl font-bold text-gray-800 font-[Dm-sans]">
                Cart
              </h2>
              <button
                onClick={onClose}
                className="p-2 sm:p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition"
              >
                <CloseIcon fontSize="medium" />
              </button>
            </div>
          </div>

          {message && (
            <div
              className={`mx-4 sm:mx-8 mt-5 p-4 rounded-xl text-white font-bold text-center shadow-lg text-sm sm:text-base ${messageType === "error" ? "bg-red-600" : "bg-green-600"}`}
            >
              {message}
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-4 sm:px-8 pb-8 pt-4 space-y-6">
            {loading && !hasLoadedOnce ? (
              <div className="flex items-center justify-center py-10">
                <video
                  src="/materialwaveloading.webm"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-40 h-40 object-contain"
                />
              </div>
            ) : firms.length === 0 ? (
              <div className="text-center py-12 text-red-600 font-bold">
                No firms found for this account
              </div>
            ) : (
              <div className="space-y-6">
                {/* Firm selector — disabled when the dealer has only ONE
                    firm on file (no need to choose, no risk of accidental
                    change). Active dropdown only when there are multiple
                    firms to pick between. */}
                <div>
                  <label className="block font-bold text-lg mb-2">
                    Select Firm <span className="text-red-500">*</span>
                  </label>
                  <select
                    ref={firmRef}
                    value={selectedFirm}
                    onChange={(e) => setSelectedFirm(e.target.value)}
                    disabled={firms.length <= 1}
                    className={`w-full px-4 py-4 rounded-xl border-2 text-base font-medium focus:outline-none focus:ring-4 focus:ring-yellow-200 ${
                      firms.length <= 1
                        ? "border-gray-300 bg-gray-100 text-gray-700 cursor-not-allowed appearance-none"
                        : "border-yellow-500 bg-yellow-50"
                    }`}
                  >
                    {firms.length > 1 && (
                      <option value="">-- Select Firm --</option>
                    )}
                    {firms.map((f) => (
                      <option key={`${f.firmName}-${f.gst}`} value={f.firmName}>
                        {f.firmName}
                      </option>
                    ))}
                  </select>
                  {showReq(!selectedFirm) && (
                    <p className="text-red-500 text-xs mt-1 font-semibold">
                      {REQ_MSG}
                    </p>
                  )}
                </div>

                {/* Firm details */}
                <div className="bg-gray-50 p-5 rounded-2xl border space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-semibold mb-2 text-sm">
                        GST No. *
                      </label>
                      <input
                        value={firmDetails.gst || "—"}
                        readOnly
                        className="w-full px-4 py-3 rounded-xl bg-white border text-gray-700"
                      />
                      {showReq(!firmDetails.gst) && (
                        <p className="text-red-500 text-xs mt-1 font-semibold">
                          {REQ_MSG}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block font-semibold mb-2 text-sm">
                        Contact Person
                      </label>
                      <input
                        value={firmDetails.contactPerson || "—"}
                        readOnly
                        className="w-full px-4 py-3 rounded-xl bg-white border text-gray-700"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
                    <div>
                      <label className="block font-semibold mb-2 text-sm">
                        Contact No. *
                      </label>
                      <input
                        value={firmDetails.contact || "—"}
                        readOnly
                        className="w-full px-4 py-3 rounded-xl bg-white border font-bold text-gray-800"
                      />
                      {showReq(!firmDetails.contact) && (
                        <p className="text-red-500 text-xs mt-1 font-semibold">
                          {REQ_MSG}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block font-bold text-green-700 mb-2 text-sm">
                        Credit Limit
                      </label>
                      <input
                        value={`₹${Number(String(firmDetails.creditLimit ?? "0").replace(/,/g, "")).toLocaleString("en-IN")}`}
                        readOnly
                        className="w-full px-4 py-3 rounded-xl bg-green-50 border border-green-400 font-bold text-green-800"
                      />
                    </div>
                    <div>
                      <label className="block font-bold text-orange-700 mb-2 text-sm">
                        Outstanding
                      </label>
                      <input
                        value={
                          fetchingLive
                            ? "Loading..."
                            : `₹${Number(firmDetails.outstanding || 0).toLocaleString("en-IN")}`
                        }
                        readOnly
                        className="w-full px-4 py-3 rounded-xl bg-orange-50 border border-orange-500 font-bold text-orange-700"
                      />
                    </div>
                    <div>
                      <label className="block font-bold text-blue-700 mb-2 text-sm">
                        Available Limit
                      </label>
                      <input
                        value={`₹${availableLimit.toLocaleString("en-IN")}`}
                        readOnly
                        className="w-full px-4 py-3 rounded-xl bg-blue-50 border border-blue-500 font-bold text-blue-700"
                      />
                    </div>
                  </div>
                </div>

                {/* Products tabs */}
                {tabDefs.length > 0 ? (
                  <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
                    <h3 className="text-base sm:text-lg p-3 font-extrabold text-gray-800 tracking-wide uppercase">
                      Products
                    </h3>
                    <div className="px-4 sm:px-5 py-3 border-b bg-gray-50">
                      <div className="flex flex-wrap gap-2">
                        {tabDefs.map((t) => {
                          const isActive = t.key === activeTab;
                          const isMissingPT =
                            submitAttempted &&
                            !String(
                              formData?.[t.key]?.paymentTerms || "",
                            ).trim();
                          return (
                            <button
                              key={t.key}
                              type="button"
                              onClick={() => setActiveTab(t.key)}
                              className={`px-4 py-2 rounded-full text-sm font-extrabold transition ${isActive ? "bg-yellow-500 text-black shadow" : "bg-white border hover:bg-gray-50 text-gray-700"} ${isMissingPT ? "ring-2 ring-red-300" : ""}`}
                              title={isMissingPT ? "Payment term missing" : ""}
                            >
                              {t.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="p-4 sm:p-5 space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        <div>
                          <label className="block font-bold mb-2 text-sm">
                            Vehicle / Transporter{" "}
                            <span className="text-gray-400 text-xs">
                              (optional)
                            </span>
                          </label>
                          <AutocompleteInput
                            value={activeForm.vehicle}
                            onChange={(v) => handleChange("vehicle", v)}
                            fieldKey="vehicle"
                            phone={storedPhone}
                            placeholder="GJ01AB1234"
                            className={`w-full px-4 py-3 rounded-xl border-2 transition text-base outline-none ${activeErrors.vehicle ? "border-red-500 focus:ring-4 focus:ring-red-100" : "border-gray-300 focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100"}`}
                          />
                          {activeErrors.vehicle && (
                            <p className="text-red-500 text-xs mt-1">
                              {activeErrors.vehicle}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block font-bold mb-2 text-sm">
                            Delivery Area{" "}
                            <span className="text-gray-400 text-xs">
                              (optional)
                            </span>
                          </label>
                          <AutocompleteInput
                            value={activeForm.deliveryArea}
                            onChange={(v) =>
                              setTabField(activeTab, "deliveryArea", v)
                            }
                            fieldKey="deliveryArea"
                            phone={storedPhone}
                            placeholder="e.g. Surat, Ahmedabad"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100 outline-none text-base"
                          />
                        </div>
                        <div>
                          <label className="block font-bold mb-2 text-sm">
                            Driver Contact{" "}
                            <span className="text-gray-400 text-xs">
                              (optional)
                            </span>
                          </label>
                          <AutocompleteInput
                            value={activeForm.driverContact}
                            onChange={(v) => handleChange("driverContact", v)}
                            fieldKey="driverContact"
                            phone={storedPhone}
                            placeholder="9876543210"
                            maxLength={10}
                            inputMode="numeric"
                            onlyDigits
                            className="w-full px-4 py-3 rounded-xl border-2 font-mono text-base transition border-gray-300 focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100 outline-none"
                          />
                        </div>
                        <div>
                          <label className="block font-bold mb-2 text-sm">
                            Loading Date{" "}
                            <span className="text-gray-400 text-xs">
                              (optional)
                            </span>
                          </label>
                          <input
                            type="date"
                            value={activeForm.loadingDate}
                            onChange={(e) =>
                              setTabField(
                                activeTab,
                                "loadingDate",
                                e.target.value,
                              )
                            }
                            min={new Date().toISOString().split("T")[0]}
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100 outline-none text-base"
                          />
                        </div>
                        <div>
                          <label className="block font-bold mb-2 text-sm">
                            Payment Terms{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <select
                            ref={(el) => (paymentRefs.current[activeTab] = el)}
                            value={activeForm.paymentTerms}
                            onChange={(e) =>
                              setTabField(
                                activeTab,
                                "paymentTerms",
                                e.target.value,
                              )
                            }
                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100 outline-none text-base font-medium"
                          >
                            <option value="">-- Select Payment Term --</option>
                            <option value="100% RTGS Against Proforma Invoice">
                              100% RTGS Against Proforma Invoice
                            </option>
                            <option value="Next Day RTGS">Next Day RTGS</option>
                            <option value="10 days RTGS Rate +300">
                              10 days RTGS Rate +300
                            </option>
                            <option value="15 days RTGS rate +450">
                              15 days RTGS rate +450
                            </option>
                            {ubsStatus && <option value="UBS">UBS</option>}
                          </select>
                          {showReq(!activeForm.paymentTerms) && (
                            <p className="text-red-500 text-xs mt-1 font-semibold">
                              {REQ_MSG}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block font-bold mb-2 text-sm">
                            Broker Name{" "}
                            <span className="text-gray-400 text-xs">
                              (optional)
                            </span>
                          </label>
                          <AutocompleteInput
                            value={activeForm.brokerName}
                            onChange={(v) =>
                              setTabField(activeTab, "brokerName", v)
                            }
                            fieldKey="brokerName"
                            phone={storedPhone}
                            placeholder="Enter broker name"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100 outline-none text-base"
                          />
                        </div>
                        <div className="lg:col-span-3 sm:col-span-2">
                          <label className="block font-bold mb-2 text-sm">
                            Remarks (Optional)
                          </label>
                          <textarea
                            rows={3}
                            value={activeForm.remarks}
                            onChange={(e) =>
                              setTabField(activeTab, "remarks", e.target.value)
                            }
                            placeholder="Any special instructions..."
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100 outline-none resize-none text-base"
                          />
                        </div>
                      </div>
                      {renderActiveCartSection()}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-2xl p-10 text-center text-gray-500 border">
                    Cart is empty
                  </div>
                )}

                {/* OTP */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-5 rounded-2xl">
                  <h3 className="text-lg font-bold text-green-800 text-center mb-4">
                    Order Verification
                  </h3>

                  {!allTabsHavePaymentTerm && submitAttempted && (
                    <div className="max-w-xl mx-auto mb-4 rounded-xl bg-red-50 border border-red-200 p-3 text-center text-sm font-bold text-red-700">
                      Please select Payment Term in every tab before sending
                      OTP.
                    </div>
                  )}
                  {!sheetSyncOk && (
                    <div className="max-w-xl mx-auto mb-4 rounded-xl bg-yellow-50 border border-yellow-200 p-3 text-center text-sm font-bold text-yellow-800">
                      Please Sync latest rate/stock before OTP & Submit.
                    </div>
                  )}
                  {DEMO_OTP_ENABLED &&
                    otpPhone === DEMO_PHONE &&
                    demoOtpValue && (
                      <div className="max-w-xl mx-auto mb-4 rounded-2xl border border-dashed border-emerald-400 bg-white p-4 text-center shadow-sm">
                        <div className="text-sm font-extrabold text-emerald-800">
                          Demo OTP (DEV Only)
                        </div>
                        <div className="mt-2 text-3xl font-black tracking-[0.35em] text-gray-900">
                          {demoOtpValue}
                        </div>
                        <div className="mt-1 text-xs font-semibold text-gray-500">
                          Works only on localhost in DEV build
                        </div>
                      </div>
                    )}

                  <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        handleSendOtp();
                      }}
                      disabled={
                        !canResend ||
                        isAnyBookingClosed ||
                        isAnyStockInvalid ||
                        !allTabsHavePaymentTerm ||
                        !sheetSyncOk ||
                        isSyncingSheet
                      }
                      className={`w-full h-[4rem] rounded-xl text-white text-base sm:text-lg shadow-xl transition-all ${canResend && !isAnyBookingClosed && !isAnyStockInvalid && allTabsHavePaymentTerm && sheetSyncOk && !isSyncingSheet ? "bg-green-600 hover:bg-green-700" : "bg-gray-400 cursor-not-allowed"}`}
                    >
                      {canResend
                        ? DEMO_OTP_ENABLED && otpPhone === DEMO_PHONE
                          ? "Show OTP (Demo)"
                          : "Send OTP on WhatsApp"
                        : `Resend in ${timer}s`}
                    </button>

                    <div className="w-full">
                      <label className="block text-sm sm:text-base font-semibold mb-2">
                        Enter 4-digit OTP{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        ref={otpRef}
                        type="text"
                        inputMode="numeric"
                        maxLength={4}
                        value={otp}
                        onChange={(e) =>
                          setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))
                        }
                        placeholder="0000"
                        className="w-full px-4 h-[4rem] rounded-xl border-2 border-gray-300 focus:border-green-600 text-center text-2xl sm:text-3xl font-mono tracking-widest outline-none transition"
                      />
                      {showReq(!otpSent) && (
                        <p className="text-red-600 text-xs mt-1 text-center font-semibold">
                          Please send OTP first.
                        </p>
                      )}
                      {showReq(otpSent && otp.length !== 4) && (
                        <p className="text-red-600 text-xs mt-1 text-center font-semibold">
                          Enter 4-digit OTP
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer buttons */}
                <div className="flex flex-col sm:flex-row justify-end gap-4 mt-8">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      onClose();
                    }}
                    className="order-2 sm:order-1 px-8 py-4 rounded-full border-2 border-gray-400 hover:bg-gray-50 font-bold text-base"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setSubmitAttempted(true);
                      if (isSubmitting) return;
                      if (!canSubmitMandatory) {
                        focusFirstMissingMandatory();
                        setMessageType("error");
                        setMessage(
                          !sheetSyncOk
                            ? "Please sync latest rate/stock first."
                            : "Please complete mandatory fields first.",
                        );
                        setTimeout(() => setMessage(""), 2500);
                        return;
                      }
                      handleSubmitOrder();
                    }}
                    disabled={!canSubmitMandatory || isSubmitting}
                    className={`order-1 sm:order-2 px-10 py-4 rounded-full font-bold text-lg shadow-2xl transition-all w-full sm:w-auto ${canSubmitMandatory && !isSubmitting ? "bg-yellow-500 hover:bg-yellow-600 text-black shadow-yellow-500/50" : "bg-gray-300 text-gray-600 cursor-not-allowed opacity-80"}`}
                    aria-disabled={!canSubmitMandatory || isSubmitting}
                    title={
                      isSubmitting
                        ? "Submitting order"
                        : !sheetSyncOk
                          ? "Sync latest rate/stock first"
                          : ""
                    }
                  >
                    {isSubmitting
                      ? "Submitting..."
                      : isSyncingSheet
                        ? "Please wait..."
                        : "Submit Order Request"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </>
  );
}
